import React from 'react';
import { motion } from 'motion/react';
import { formatSlotDuration, formatTimeWithNextDay, formatTime } from '@/lib/utils';

export interface GlobalTimeSlotTabsProps {
  timeSlots: { start_time: string; duration: number }[];
  selectedTimeSlot: string | null;
  onTimeSlotSelect: (timeSlotKey: string) => void;
  businessTimeZone?: string;
  slotsWithCounts?: Map<string, number>;
  eventStartTime?: string;
}

const TimeSlotTabs: React.FC<GlobalTimeSlotTabsProps> = ({
  timeSlots,
  selectedTimeSlot,
  onTimeSlotSelect,
  businessTimeZone,
  slotsWithCounts,
  eventStartTime,
}) => {
  // Helper to get time slot key
  const getTimeSlotKey = (startTime: string): string => {
    return new Date(startTime).toISOString();
  };

  // Helper to calculate end time (duration is in nanoseconds)
  const calculateEndTime = (startTime: string, durationNanoseconds: number): string => {
    const start = new Date(startTime);
    // Convert nanoseconds to milliseconds: nanoseconds / 1e6
    const end = new Date(start.getTime() + durationNanoseconds / 1e6);
    return end.toISOString();
  };

  // Scroll state
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Check scroll capability
  const checkScroll = React.useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1); // -1 tolerance
    }
  }, []);

  // Listen for resize and scroll events
  React.useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      // Check initially
      checkScroll();
      
      const observer = new ResizeObserver(checkScroll);
      observer.observe(container);
      
      return () => {
        container.removeEventListener('scroll', checkScroll);
        observer.disconnect();
      };
    }
  }, [checkScroll, timeSlots]); // Re-check when timeSlots change

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (timeSlots.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No time slots available for this event.</p>
      </div>
    );
  }

  const doFormatTime = (time: string) => eventStartTime 
    ? formatTimeWithNextDay(time, eventStartTime, businessTimeZone)
    : formatTime(time, businessTimeZone);

  return (
    <div className="space-y-4 flex flex-col items-center">
      <h3 className="text-xl md:text-2xl font-bold font-serif text-white tracking-tight mb-4 text-center">Select a Time Slot</h3>
      
      {/* Scrollable Container Wrapper with Group for hover detection */}
      <div className="relative group/scroll w-full">
        {/* Left Arrow */}
        {canScrollLeft && (
          <div className="absolute left-0 top-0 bottom-[2px] w-12 hidden md:flex items-center justify-start z-10 pointer-events-none transition-opacity duration-300">
            <button
              onClick={() => scroll('left')}
              className="pointer-events-auto ml-1 p-2.5 rounded-full bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 transition-transform hover:scale-110 shadow-md"
              aria-label="Scroll left"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          </div>
        )}

        {/* Right Arrow */}
        {canScrollRight && (
          <div className="absolute right-0 top-0 bottom-[2px] w-12 hidden md:flex items-center justify-end z-10 pointer-events-none transition-opacity duration-300">
            <button
              onClick={() => scroll('right')}
              className="pointer-events-auto mr-1 p-2.5 rounded-full bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 transition-transform hover:scale-110 shadow-md"
              aria-label="Scroll right"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Tab Bar Container with Horizontal Scroll */}
        <div 
          ref={scrollContainerRef}
          className="overflow-x-auto -mx-6 px-6 scrollbar-hide"
        >
          <div className="flex py-2 min-w-max justify-center">
            {timeSlots.map((slot) => {
              const timeSlotKey = getTimeSlotKey(slot.start_time);
              const isSelected = selectedTimeSlot === timeSlotKey;
              const endTime = calculateEndTime(slot.start_time, slot.duration);

              return (
                  <motion.button
                    key={timeSlotKey}
                    onClick={() => onTimeSlotSelect(timeSlotKey)}
                    whileHover={!isSelected ? { backgroundColor: 'rgba(31, 41, 55, 0.5)', scale: 1.02 } : { scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className={`
                      relative px-5 sm:px-6 py-2.5 sm:py-3 mx-1 rounded-lg transition-all duration-200 border
                      ${isSelected
                        ? 'bg-blue-500/20 text-blue-400 border-blue-500/50 shadow-[0_0_15px_rgba(59,130,246,0.2)] font-bold'
                        : 'bg-gray-800/80 text-gray-300 border-white/5 hover:text-white hover:border-white/10 font-semibold shadow-sm'
                      }
                    `}
                    title={`Duration: ${formatSlotDuration(slot.duration)} | Ends at: ${doFormatTime(endTime)}`}
                  >
                    {slotsWithCounts?.has(timeSlotKey) && (
                      <div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-emerald-400" />
                    )}
                    <div className="flex flex-col items-center justify-center gap-1 min-w-[55px] sm:min-w-[70px] relative">
                      <div className="flex items-center justify-center relative">
                        {(() => {
                          const timeStr = doFormatTime(slot.start_time);
                          const hasNextDay = timeStr.includes('+1');
                          const baseTime = timeStr.replace('+1', '');
                          return (
                            <span className={`text-sm sm:text-base whitespace-nowrap ${isSelected ? 'font-bold text-blue-400' : 'font-semibold'}`}>
                              {baseTime}
                              {hasNextDay && <sup className={`ml-0.5 text-[10px] sm:text-xs font-bold ${isSelected ? 'text-blue-400' : 'text-gray-400'}`}>+1</sup>}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </motion.button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotTabs;

